import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ChevronRight } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { faqData } from '@/constants/faqData';
import { getPublicPageTopPadding, getStandardTopPadding } from '@/lib/layout-utils';
import { useUser } from '@/contexts/UserContext';

const FAQ = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();
  const topPadding = isAuthenticated ? getStandardTopPadding() : getPublicPageTopPadding();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className={`min-h-screen bg-background ${topPadding}`}>
      <SEOHead
        title="Frequently Asked Questions - AI-Powered Family Meal Planning"
        description="Get answers to common questions about Forkast, the AI-powered family meal planning app. Learn how AI suggestions work, organize meals, share with family, and build better eating habits."
        jsonLd={jsonLd}
      />
      
      {/* Hero Section */}
      <section className="py-8 md:py-12 bg-gradient-to-br from-background via-cream/20 to-sunset/5">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-center mb-6">
              <h1 className="text-3xl md:text-4xl font-bold font-playfair text-charcoal mb-4">
                Frequently Asked Questions
              </h1>
              <p className="text-lg text-muted-foreground font-inter leading-relaxed max-w-2xl mx-auto">
                Everything parents ask about forkast's weekly meal planning app. Learn how to plan family meals easily.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pt-8 md:pt-12 pb-8 md:pb-12 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq) => (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className="border border-sage/20 rounded-lg px-6 py-2 bg-cream/50"
                >
                  <AccordionTrigger className="text-left text-lg font-medium font-playfair text-charcoal hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground font-inter leading-relaxed pt-2">
                    <div 
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('/')) {
                          e.preventDefault();
                          const href = target.getAttribute('href');
                          if (href) {
                            navigate(href);
                          }
                        }
                      }}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-6 md:py-8 bg-gradient-to-br from-background via-cream/20 to-sunset/5">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-playfair text-charcoal mb-6">
              Start your family meal planning today
            </h2>
            <p className="text-xl text-muted-foreground font-inter mb-8 leading-relaxed">
              Use forkast to save your favorite recipes, create a weekly meal plan, and simplify grocery shopping.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth/register">
                <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium">
                  Signup for free
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/how-forkast-meal-planning-works">
                <Button variant="outline" size="lg" className="px-8 py-4 border-sunset text-sunset hover:bg-sunset hover:text-white font-inter font-medium">
                  How it works
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;